import React, { useContext, useMemo, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Button,
  Snackbar,
  Alert,
  ListSubheader,
  IconButton,
  Modal,
  Divider,
} from "@mui/material";
import ComplianceBox from "./Components/ComplianceBox";
import ErrorUI from "./Components/ErrorUI";
import { useAccounts } from "../../components/Common/HelperComponents/GetDataFromStore";
import jwt_decode from "jwt-decode";
import { Container } from "reactstrap";
import SearchIcon from "@mui/icons-material/Search";
import ListScans from "./Components/ListScans";
import FilterListIcon from '@mui/icons-material/FilterList';
import { formatUtcDateSimple } from "../../helpers/formatUtcDate";
import { getComplianceList, clearAllComplianceMessages, deleteComplianceWorkload, initiateComplianceScan } from "../../store/compliance/complianceSlice";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import CreateWorkloadModal from './Components/CreateWorkloadModal';
import { styled } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EditWorkloadModal from './Components/EditWorkloadModal';
import InitiateScanModal from './Components/InitiateScanModal';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { CloudProviderContext, getAccountCloudType } from "../../CloudProviderContext";
import { getListOfOrgsAndAccts } from "../../store/actions";
import CloudProviderMark from "../../components/Common/CloudProviderMark";
import {
  AH_ACCENT,
  AH_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  AuditHubThemeProvider,
  auditHubPageSx,
  outlinedButtonSx,
} from "./AuditHub/theme";

const ComplianceDashboard = () => {
  const { selectedProvider } = useContext(CloudProviderContext);
  const dispatch = useDispatch();
  const accounts = useAccounts();
  const [isAccountConfigured, setIsAccountConfigured] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [scansModalOpen, setScansModalOpen] = useState(false);
  const [selectedScan, setSelectedScan] = useState(null);
  const [currentComplianceData, setCurrentComplianceData] = useState([]);
  const [selectedWorkload, setSelectedWorkload] = useState("");
  const [initiateScanModalOpen, setInitiateScanModalOpen] = useState(false);
  const [createWorkloadModalOpen, setCreateWorkloadModalOpen] = useState(false);
  const [selectValue, setSelectValue] = useState(selectedWorkload || selectedAccount);
  const [availableDates, setAvailableDates] = useState([]);
  const [complianceResultOrderedMap, setComplianceResultOrderedMap] = useState({});
  const [selectedScanDate, setSelectedScanDate] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredAccount, setHoveredAccount] = useState(null);
  const [selectOpen, setSelectOpen] = useState(false);
  const [editWorkloadModalOpen, setEditWorkloadModalOpen] = useState(false);
  const [selectedWorkloadToEdit, setSelectedWorkloadToEdit] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedWorkloadToDelete, setSelectedWorkloadToDelete] = useState(null);
  const [expandedAccount, setExpandedAccount] = useState(null);
  const apiCallInProgress = useRef(false);

  // Selectors
  const { 
    isGettingComplianceList, 
    getComplianceListError, 
    getComplianceListMessage,
    complianceList,
    isDeletingComplianceWorkload,
    deleteComplianceWorkloadMessage,
    deleteComplianceWorkloadError,
    isInitiatingComplianceScan,
    initiateComplianceScanMessage,
    initiateComplianceScanError
  } = useSelector((state) => state.compliance);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  function safeDispatchComplianceList(payload) {
    if (apiCallInProgress.current) return;
    apiCallInProgress.current = true;
    dispatch(getComplianceList(payload));
    setTimeout(() => { apiCallInProgress.current = false; }, 300);
  }

  const providerAccounts = useMemo(() => {
    if (!accounts) return [];
    return accounts.filter(
      (acc) =>
        !acc.accountNumber.includes("INIT") &&
        getAccountCloudType(acc) === selectedProvider
    );
  }, [accounts, selectedProvider]);

  useEffect(() => {
    let userInfo;
    if (localStorage.getItem("authUser")) {
      const obj = JSON.parse(localStorage.getItem("authUser"));
      userInfo = jwt_decode(obj.idToken).email;
    }
    if (!accounts) return;

    const activeAccounts = providerAccounts.filter(
      (account) =>
        account.status === "ACTIVE" &&
        account.listusr?.some((user) => user.userName === userInfo)
    );

    if (activeAccounts.length > 0) {
      setIsAccountConfigured(true);
      setAccountStatus("ACTIVE");
    } else {
      setIsAccountConfigured(false);
      const inactiveAccounts = providerAccounts.filter(
        (account) =>
          account.status === "INACTIVE" && account.number !== "INIT00000001"
      );
      if (inactiveAccounts.length > 0) {
        setAccountStatus("INACTIVE");
      } else {
        setAccountStatus("NOACCOUNT");
      }
    }
  }, [accounts, providerAccounts, selectedProvider]);

  useEffect(() => {
    dispatch(
      getListOfOrgsAndAccts({ params: { accountType: selectedProvider } })
    );

    setSelectedAccount("");
    setSelectedWorkload("");
    setSelectValue("");
    setSelectedScan(null);
    setCurrentComplianceData([]);
    setAvailableDates([]);
    setComplianceResultOrderedMap({});
    setSelectedScanDate(null);
    setExpandedAccount(null);
  }, [selectedProvider, dispatch]);

  // Fetch compliance data when account changes
  useEffect(() => {
    if (selectedAccount) {
      safeDispatchComplianceList({ accountNumber: selectedAccount });
    }
  }, [selectedAccount, dispatch]);

  // Handle API responses and errors
  useEffect(() => {
    // Only ignore when accounts have loaded and the selection is not in this provider list.
    // Do not treat "accounts still loading" (empty providerAccounts) as a mismatch — that
    // drops the checks response and leaves the UI empty until the user re-selects.
    if (
      selectedAccount &&
      providerAccounts.length > 0 &&
      !providerAccounts.some((acc) => acc.accountNumber === selectedAccount)
    ) {
      return;
    }
    if (getComplianceListMessage && complianceList) {
      console.log("🚀 ~ useEffect ~ complianceList:", complianceList)
      setAvailableDates(complianceList.availableDates || []);
      setComplianceResultOrderedMap(complianceList.complianceResultOrderedMap || {});
      
      let scans = Object.entries(complianceList.complianceResultOrderedMap || {})
        .map(([key, scan]) => ({ ...scan, _key: key }));

      if (!selectedWorkload) {
        // Account view: filter for Full Run
        scans = scans.filter(scan =>
          scan.runStatus === "Complete" &&
          scan.resourceScopeType === "Full Run"
        );
      } else {
        // Workload view: filter for Complete only
        scans = scans.filter(scan =>
          scan.runStatus === "Complete"
        );
      }

      // Sort by scanDate descending (most recent first)
      scans.sort((a, b) => new Date(b.scanDate) - new Date(a.scanDate));

      const latestFullRunScan = scans[0];

      if (latestFullRunScan) {
        console.log("🚀 ~ useEffect ~ latestFullRunScan:", latestFullRunScan)
        setSelectedScan(latestFullRunScan);
        setSelectedScanDate(latestFullRunScan.scanDate);
        setCurrentComplianceData(latestFullRunScan.complianceOrderedList || []);
      } else {
        setSelectedScan(null);
        setSelectedScanDate(null);
        setCurrentComplianceData([]);
      }
    }

    if (getComplianceListError) {
      setSnackbar({
        open: true,
        message: getComplianceListError,
        severity: "error",
        autoHideDuration: 6000,
      });
    }

    // Only clear success messages, not error messages
    if (getComplianceListMessage) {
      dispatch(clearAllComplianceMessages());
    }
  }, [
    getComplianceListMessage,
    getComplianceListError,
    complianceList,
    dispatch,
    selectedAccount,
    providerAccounts,
    selectedWorkload,
  ]);

  // Handle delete success/error
  useEffect(() => {
    if (deleteComplianceWorkloadMessage) {
      setSnackbar({
        open: true,
        message: deleteComplianceWorkloadMessage,
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      // Refresh compliance list
      if (selectedAccount) {
        safeDispatchComplianceList({ accountNumber: selectedAccount });
      }
    } else if (deleteComplianceWorkloadError) {
      setSnackbar({
        open: true,
        message: deleteComplianceWorkloadError,
        severity: 'error',
      });
    }
  }, [deleteComplianceWorkloadMessage, deleteComplianceWorkloadError, selectedAccount, dispatch]);

  // Handle scan selection
  const handleScanSelect = (scan) => {
    setSelectedScan(scan);
    setCurrentComplianceData(scan.complianceOrderedList);
  };

  // Filter out INIT accounts and sort by creation date (descending)
  const accountsWithWorkloads = useMemo(() => {
    let filtered = [...providerAccounts];
    if (filtered.length && filtered[0].creationDate) {
      filtered = filtered.sort(
        (a, b) => new Date(b.creationDate) - new Date(a.creationDate)
      );
    }
    const accountWorkloadMap = complianceList?.accountWorkloadMap || {};
    return filtered.map((acc) => ({
      ...acc,
      schedules: accountWorkloadMap[acc.id] || [],
    }));
  }, [providerAccounts, complianceList]);

  // Set default selected account on mount / when accounts become available
  useEffect(() => {
    if (!selectedAccount && accountsWithWorkloads.length > 0) {
      const accountNumber = accountsWithWorkloads[0].accountNumber;
      setSelectedAccount(accountNumber);
      setSelectedWorkload("");
      // Same explicit fetch as dropdown click so data loads on initial page entry
      safeDispatchComplianceList({ accountNumber });
    }
  }, [accountsWithWorkloads, selectedAccount]);

  useEffect(() => {
    setSelectValue(selectedWorkload || selectedAccount);
  }, [selectedWorkload, selectedAccount]);

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setHoveredAccount(null);
  };


  const handleDropdownChange = (e) => {
    const value = e.target.value;
   
    // If value is an account, clear workload
    const isAccount = accountsWithWorkloads.some(acc => acc.accountNumber === value);
    if (isAccount) {
      setSelectedAccount(value);
      setSelectedWorkload("");
      setSelectValue(value);
    } else {
      // Find the parent account
      const parentAcc = accountsWithWorkloads.find(acc => 
        acc.schedules.some(schedule => schedule.scheduleId === value)
      );
      setSelectedAccount(parentAcc?.accountNumber || "");
      setSelectedWorkload(value);
      setSelectValue(value);
    }
  };


  // Restore renderAccountMessage
  const renderAccountMessage = () => {
    if (isAccountConfigured === null || isAccountConfigured) return null;
    const providerLabel = selectedProvider === "AZURE" ? "Azure" : "AWS";
    const messageContent =
      accountStatus === "INACTIVE" ? (
        <>
          All the {providerLabel} accounts in this organization are inactive. Please add a
          new {providerLabel} account or activate the inactive accounts through the{" "}
          <Typography
            component="span"
            onClick={() => (window.location.href = "/configuration/accounts")}
            sx={{
              color: "primary.main",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            configuration
          </Typography>{" "}
          menu on the left.
        </>
      ) : (
        <>
          This organization does not have any {providerLabel} account on-boarded. Please add
          a {providerLabel} account through{" "}
          <Typography
            component="span"
            onClick={() => (window.location.href = "/configuration/accounts")}
            sx={{
              color: "primary.main",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            configuration
          </Typography>{" "}
          menu on the left.
        </>
      );
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.100",
          p: 3,
        }}
      >
        <Box
          sx={{
            maxWidth: "600px",
            width: "100%",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 1,
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{ color: "text.primary", mb: 2, fontWeight: 500 }}
          >
            Account Configuration Required
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", lineHeight: 1.6 }}
          >
            {messageContent}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Handle scan initiation success/error
  useEffect(() => {
    if (initiateComplianceScanMessage) {
      setSnackbar({
        open: true,
        message: initiateComplianceScanMessage,
        severity: 'success',
      });
      // Optionally refresh compliance list
      if (selectedAccount) {
        safeDispatchComplianceList({ accountNumber: selectedAccount });
      }
      dispatch(clearAllComplianceMessages());
      setInitiateScanModalOpen(false);
    } else if (initiateComplianceScanError) {
      setSnackbar({
        open: true,
        message: initiateComplianceScanError,
        severity: 'error',
      });
      dispatch(clearAllComplianceMessages());
    }
  }, [initiateComplianceScanMessage, initiateComplianceScanError, selectedAccount, dispatch]);

  // Expand submenu by default for the first account with workloads/schedules
  useEffect(() => {
    if (accountsWithWorkloads.length > 0) {
      const firstWithSubmenu = accountsWithWorkloads.find(acc => (acc.schedules && acc.schedules.length > 0));
      if (firstWithSubmenu) {
        setExpandedAccount(firstWithSubmenu.accountNumber);
      } else {
        setExpandedAccount(null);
      }
    }
  }, [accountsWithWorkloads]);

  // Early return if account is not configured
  if (!isAccountConfigured) {
    return (
      <AuditHubThemeProvider>
        <div className="page-content" style={{ backgroundColor: AH_WHITE }}>
          <Box sx={auditHubPageSx}>{renderAccountMessage()}</Box>
        </div>
      </AuditHubThemeProvider>
    );
  }

  console.log('Filtered compliance data:', currentComplianceData);

  // Debug: Log accounts data structure
  console.log('Accounts with workloads:', accountsWithWorkloads);
  console.log('Selected account:', selectedAccount);
  if (selectedAccount) {
    const selectedAcc = accountsWithWorkloads.find(acc => acc.accountNumber === selectedAccount);
    console.log('Selected account data:', selectedAcc);
    console.log('Selected account compliance addons:', selectedAcc?.complianceAddOns);
  }

  // Add this helper to get display name for selected value
  const getSelectedDisplay = (val) => {
    if (!val) return '';
    let label = val;
    // If selectedWorkload is set, find its name
    if (selectedWorkload) {
      // Search workloads
      for (const acc of accountsWithWorkloads) {
        const wl = acc.schedules.find(s => s.scheduleId === selectedWorkload);
        if (wl) {
          label = wl.scheduleName;
          break;
        }
      }
      if (label === val) label = selectedWorkload;
    } else {
      // Otherwise, show account name/number
      const acc = accountsWithWorkloads.find(a => a.accountNumber === selectedAccount);
      if (acc) label = `${acc.accountName} (${acc.accountNumber})`;
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
        <CloudProviderMark provider={selectedProvider} sx={{ mr: 0.75 }} />
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Box>
      </Box>
    );
  };

  // Handle edit workload
  const handleEditWorkload = (workload) => {
    setSelectedWorkloadToEdit(workload);
    setEditWorkloadModalOpen(true);
    handleMenuClose();
  };

  // Handle delete workload
  const handleDeleteWorkload = (workload) => {
    setSelectedWorkloadToDelete(workload);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  // Handle confirm delete
  const handleConfirmDelete = () => {
    if (selectedWorkloadToDelete) {
      dispatch(deleteComplianceWorkload({ 
        workloadScheduleName: selectedWorkloadToDelete.scheduleName
      }));
    }
  };

  // Add this helper function before the return statement
  function getAccountNumberForWorkload(workload) {
    if (!workload) return '';
    for (const acc of accountsWithWorkloads) {
      if (acc.schedules.some(s => s.scheduleId === workload.scheduleId)) {
        return acc.accountNumber;
      }
    }
    return '';
  }

  // Helper function to check if a compliance framework is enabled for the selected account
  const isComplianceEnabled = (complianceName) => {
    const selectedAccountData = accountsWithWorkloads.find(acc => acc.accountNumber === selectedAccount);
    if (!selectedAccountData || !selectedAccountData.complianceAddOns) {
      return false; // Not enabled until account addon data is available
    }
    
    const complianceAddon = selectedAccountData.complianceAddOns.find(addon => addon.name === complianceName);
    return complianceAddon ? complianceAddon.enabled : false; // Default to false if not found
  };

  return (
    <AuditHubThemeProvider>
      <div className="page-content" style={{ backgroundColor: AH_WHITE }}>
        <Container fluid>
      <Box
        key={selectedProvider}
        sx={{
          ...auditHubPageSx,
          pt: 1.5,
          pb: 4,
          width: "100%",
          backgroundColor: AH_WHITE,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Filters Row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            mb: 2,
            gap: 2,
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            {accountsWithWorkloads.length > 0 && (
              <FormControl
                size="small"
                sx={{
                  minWidth: 250,
                  background: AH_WHITE,
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                }}
              >
                <InputLabel
                  id="account-select-label"
                  sx={{
                    fontWeight: 600,
                    color: AH_MUTED,
                    fontSize: "0.875rem",
                  }}
                >
                  Account / Workload
                </InputLabel>
                <Select
                  labelId="account-select-label"
                  value={selectValue}
                  label="Account / Workload"
                  onChange={handleDropdownChange}
                  renderValue={getSelectedDisplay}
                  onOpen={() => setSelectOpen(true)}
                  onClose={() => setSelectOpen(false)}
                  open={selectOpen}
                  sx={{
                    fontWeight: 600,
                    color: AH_TEXT,
                    fontSize: "0.875rem",
                    background: AH_WHITE,
                    borderRadius: "8px",
                    boxShadow: "none",
                    minHeight: 40,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: AH_BORDER },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: AH_TEXT, borderWidth: "1px" },
                    "& .MuiSelect-select": {
                      padding: "8px 14px",
                      borderRadius: "8px",
                      background: AH_WHITE,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: "12px",
                        boxShadow: "none",
                        mt: 1,
                        background: AH_WHITE,
                        border: `1px solid ${AH_BORDER}`,
                        minWidth: 320,
                      },
                    },
                  }}
                >
                  {accountsWithWorkloads.map(acc => (
                    <React.Fragment key={acc.accountNumber}>
                      <MenuItem
                        value={acc.accountNumber}
                        sx={{
                          pl: 2,
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: `1px solid ${AH_BORDER}`,
                          background: selectValue === acc.accountNumber ? "#fff4eb" : "inherit",
                        }}
                        onClick={e => {
                          // Only select account if not clicking a workload
                          if (e.target.closest('.workload-submenu-item')) return;
                          setSelectedAccount(acc.accountNumber);
                          setSelectedWorkload("");
                          setSelectValue(acc.accountNumber);
                          setExpandedAccount(expandedAccount === acc.accountNumber ? null : acc.accountNumber);
                          safeDispatchComplianceList({ accountNumber: acc.accountNumber });
                          setSelectOpen(false);
                        }}
                      >
                        <ListItemText primary={`${acc.accountName} (${acc.accountNumber})`} />
                        {(acc.schedules?.length > 0) && (
                          <IconButton
                            size="small"
                            onClick={e => {
                              e.stopPropagation();
                              setExpandedAccount(expandedAccount === acc.accountNumber ? null : acc.accountNumber);
                            }}
                            sx={{ ml: 1 }}
                          >
                            {expandedAccount === acc.accountNumber ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                      </MenuItem>
                      {/* Inline expandable submenu for schedules */}
                      {expandedAccount === acc.accountNumber && acc.schedules.length > 0 && (
                        <Box sx={{
                          pl: 5,
                          py: 1,
                          background: AH_TRACK,
                          borderLeft: `3px solid ${AH_ACCENT}`,
                          borderRadius: 1,
                          mb: 1,
                        }}>
                          <Typography variant="caption" sx={{ color: AH_MUTED, fontWeight: 700, mb: 0.5, pl: 0.5 }}>
                            Workloads
                          </Typography>
                          {acc.schedules.map((item) => (
                            <Box
                              key={item.scheduleId}
                              className="workload-submenu-item"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                py: 0.5,
                                px: 1,
                                borderRadius: 1,
                                background: selectValue === item.scheduleId ? "#fff4eb" : "transparent",
                                mt: 0.5,
                                '&:hover': {
                                  background: AH_TRACK,
                                },
                                borderBottom: `1px solid ${AH_BORDER}`,
                                minHeight: 36,
                                cursor: 'pointer',
                              }}
                              onClick={e => {
                                e.stopPropagation();
                                setSelectedAccount(acc.accountNumber);
                                setSelectedWorkload(item.scheduleId);
                                setSelectValue(item.scheduleId);
                                safeDispatchComplianceList({ accountNumber: acc.accountNumber, workloadScheduleName: item.scheduleName });
                                setSelectOpen(false);
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: AH_TEXT,
                                  pl: 1,
                                  flex: 1,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  pr: 2,
                                }}
                                title={item.scheduleName}
                              >
                                {item.scheduleName}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, minWidth: 48, justifyContent: 'flex-end' }}>
                                <IconButton
                                  size="small"
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleEditWorkload(item);
                                  }}
                                  sx={{ color: AH_ACCENT, p: '4px', '&:hover': { bgcolor: "#fff4eb" } }}
                                >
                                  <EditIcon fontSize="small" style={{ fontSize: 18 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleDeleteWorkload(item);
                                  }}
                                  sx={{ color: '#d32f2f', p: '4px', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.08)' } }}
                                >
                                  <DeleteIcon fontSize="small" style={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </React.Fragment>
                  ))}
                </Select>
              </FormControl>
            )}
            {/* Scan Date Box */}
            <Paper
              elevation={0}
              sx={{
                px: 2.5,
                py: 1.2,
                borderRadius: "8px",
                background: AH_WHITE,
                fontWeight: 600,
                fontSize: "0.875rem",
                color: AH_TEXT,
                border: `1px solid ${AH_BORDER}`,
                boxShadow: "none",
                display: "flex",
                alignItems: "center",
                minHeight: 40,
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.8,
                },
                gap: 1.2,
              }}
              onClick={() => setScansModalOpen(true)}
            >
              <CalendarMonthIcon sx={{ color: AH_MUTED, fontSize: 20, mr: 1 }} />
              <Box
                component="span"
                sx={{
                  fontWeight: 500,
                  color: AH_TEXT,
                  fontSize: "0.875rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {selectedScan?.scanDate
                  ? formatUtcDateSimple(selectedScan.scanDate)
                  : "No scan"}
                <FilterListIcon sx={{ fontSize: 16, ml: 0.5, color: AH_MUTED }} />
              </Box>
            </Paper>
          </Box>
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Create Workload Button — AWS only */}
            {selectedProvider !== "AZURE" && (
              <Tooltip title="Create Workload" arrow>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateWorkloadModalOpen(true)}
                  sx={outlinedButtonSx}
                >
                  Workload
                </Button>
              </Tooltip>
            )}
            {/* Configure Scan Button */}
            <Tooltip title="Initiate Scan" arrow>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PlayArrowIcon sx={{ fontSize: 24 }} />}
                sx={outlinedButtonSx}
                onClick={() => setInitiateScanModalOpen(true)}
              >
                Initiate Scan
              </Button>
            </Tooltip>
          </Box>
        </Box>
        {/* Compliance Boxes Grid */}
        {isGettingComplianceList ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="40vh"
          >
            <CircularProgress color="primary" />
          </Box>
        ) : getComplianceListError ? (
          <ErrorUI 
            error={getComplianceListError}
            onRetry={() => safeDispatchComplianceList({ accountNumber: selectedAccount })}
          />
        ) : currentComplianceData.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight="40vh"
            width="100%"
            sx={{ background: 'transparent' }}
          >
            <Box mb={2}>
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="32" fill="#F5F7FA"/>
                <path d="M20 40C20 36.6863 25.3726 34 32 34C38.6274 34 44 36.6863 44 40" stroke="#B0BEC5" strokeWidth="2.5" strokeLinecap="round"/>
                <ellipse cx="32" cy="26" rx="6" ry="6" fill="#B0BEC5"/>
              </svg>
            </Box>
            <Typography variant="h6" color="text.secondary" fontWeight={500} mb={1}>
              No compliance data available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try selecting a different account or workload, or initiate a new scan.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={
              currentComplianceData.length <= 2
                ? {
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 3,
                    width: "100%",
                  }
                : {
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                    gap: { xs: 2, sm: 3, md: 4 },
                    width: "100%",
                    alignItems: "flex-start",
                  }
            }
          >
            {currentComplianceData
              .slice() // create a shallow copy
              .sort((a, b) => {
                // First, sort by enabled status (enabled first)
                const aEnabled = isComplianceEnabled(a.name);
                const bEnabled = isComplianceEnabled(b.name);
                
                if (aEnabled !== bEnabled) {
                  return aEnabled ? -1 : 1; // enabled first
                }
                
                // Then sort alphabetically within each group
                return (a.name || '').localeCompare(b.name || '');
              })
              .map((item, idx) => (
                <ComplianceBox
                  key={idx}
                  {...item}
                  percent={item.percent}
                  runId={selectedScan?.runId}
                  scanDate={selectedScan?.scanDate}
                  accountNumber={selectedAccount}
                  isEnabled={isComplianceEnabled(item.name)}
                  suppressed_checks={item.suppressed_checks || 0}
                  suppressed_controls={item.suppressed_controls || 0}
                />
              ))}
          </Box>
        )}

        {/* ListScans Modal */}
        <ListScans
          open={scansModalOpen}
          onClose={() => setScansModalOpen(false)}
          scans={Object.values(complianceResultOrderedMap || {}).filter(scan => scan && scan.scanDate).map(scan => ({
            scanDate: scan.scanDate,
            runType: scan.runType,
            runId: scan.runId,
            complianceOrderedList: scan.complianceOrderedList,
            scheduleType: scan.scheduleType,
            runStatus: scan.runStatus,
            resourceScopeType: scan.resourceScopeType
          }))}
          selectedAccount={selectedAccount}
          onScanSelect={handleScanSelect}
          availableDates={availableDates}
          complianceResultOrderedMap={complianceResultOrderedMap}
          nextRunDate={Object.values(complianceResultOrderedMap || {}).filter(scan => scan && scan.scanDate)[0]?.nextRunDate}
        />

        {/* Create Workload Modal */}
        <CreateWorkloadModal
          open={createWorkloadModalOpen}
          onClose={() => setCreateWorkloadModalOpen(false)}
          accounts={accountsWithWorkloads}
          onSuccess={(message) => {
            setSnackbar({
              open: true,
              message: message,
              severity: 'success'
            });
            // Refresh compliance list data
            if (selectedAccount) {
              safeDispatchComplianceList({ accountNumber: selectedAccount });
            }
          }}
        />

        {/* Initiate Scan Modal */}
        <InitiateScanModal
          open={initiateScanModalOpen}
          onClose={() => setInitiateScanModalOpen(false)}
          accountsWithWorkloads={accountsWithWorkloads}
          selectedAccount={selectedAccount}
          selectedWorkload={selectedWorkload}
          onSuccess={(message) => {
            setSnackbar({
              open: true,
              message: message,
              severity: 'success'
            });
            if (selectedAccount) {
              safeDispatchComplianceList({ accountNumber: selectedAccount });
            }
          }}
        />

        {/* Edit Workload Modal */}
        <EditWorkloadModal
          open={editWorkloadModalOpen}
          onClose={() => setEditWorkloadModalOpen(false)}
          workload={selectedWorkloadToEdit}
          accountNumber={getAccountNumberForWorkload(selectedWorkloadToEdit)}
          onSuccess={(message) => {
            setSnackbar({
              open: true,
              message: message,
              severity: 'success'
            });
            if (selectedAccount) {
              safeDispatchComplianceList({ accountNumber: selectedAccount });
            }
          }}
        />

        {/* Delete Dialog */}
        <Modal
          open={deleteDialogOpen}
          onClose={() => {
            if (!isDeletingComplianceWorkload) {
              setDeleteDialogOpen(false);
            }
          }}
          aria-labelledby="delete-workload-dialog"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              borderRadius: '10px',
              boxShadow: 24,
              p: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#F5F5F5',
                padding: '16px 24px',
                borderTopLeftRadius: '10px',
                borderTopRightRadius: '10px',
                borderBottom: '1px solid #E0E0E0',
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Delete Workload
              </Typography>
              <IconButton 
                onClick={() => {
                  if (!isDeletingComplianceWorkload) {
                    setDeleteDialogOpen(false);
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider />
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Are you sure you want to delete the workload<br />
                <b>{selectedWorkloadToDelete?.scheduleName}</b>?
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => {
                    if (!isDeletingComplianceWorkload) {
                      setDeleteDialogOpen(false);
                    }
                  }}
                  disabled={isDeletingComplianceWorkload}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleConfirmDelete}
                  disabled={isDeletingComplianceWorkload}
                  startIcon={isDeletingComplianceWorkload ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {isDeletingComplianceWorkload ? 'Deleting...' : 'Delete'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
        </Container>
      </div>
    </AuditHubThemeProvider>
  );
};

export default ComplianceDashboard;
